import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
    LayoutDashboard,
    RefreshCw,
    Target,
    Star,
    BarChart3,
    Award,
    ChevronLeft
} from 'lucide-react';
import ModuleLayout from '../../layouts/ModuleLayout';
import Drawer from '../../components/common/Drawer';
import PermissionGate from '../../components/common/PermissionGate/PermissionGate';

import {
    PerformanceCycles,
    GoalManagement,
    ReviewManagement,
    PerformanceOutcomes,
    PerformanceCycleDetail,
    GoalDetail
} from './performance';

// Access Denied Component
const AccessDenied = () => (
    <div className="flex flex-col items-center justify-center p-12 text-center text-gray-500 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p>You do not have permission to view this section.</p>
    </div>
);

const PerformanceModule = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Map paths to tabs
    const getTabFromPath = (path) => {
        if (path.includes('/performance_dashboard')) return 'cycles';
        if (path.includes('/performance_analytics')) return 'cycles';
        if (path.includes('/performance_cycles')) return 'cycles';
        if (path.includes('/goal_setting')) return 'goals';
        if (path.includes('/reviews')) return 'reviews';
        if (path.includes('/outcomes')) return 'outcomes';
        return null;
    };

    // Map paths to drawer actions
    const getDrawerActionFromPath = (path) => {
        if (path.includes('/performance_cycle_detail/')) {
            const parts = path.split('/');
            const idIndex = parts.indexOf('performance_cycle_detail') + 1;
            const id = parts[idIndex];
            if (path.endsWith('/edit')) {
                // For editCycle, we now render as a full page, so return null here
                return null;
            }
            // For viewCycle, we now render as a full page, so return null here
            // to prevent the drawer from opening
            return null;
        }
        return null;
    };

    // Sync state from path if query params are missing
    useEffect(() => {
        const tabFromPath = getTabFromPath(location.pathname);
        const drawerFromPath = getDrawerActionFromPath(location.pathname);

        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        if (tabFromPath && !searchParams.get('tab')) {
            newParams.set('tab', tabFromPath);
            changed = true;
        }

        if (drawerFromPath && !searchParams.get('action')) {
            newParams.set('action', drawerFromPath.action);
            if (drawerFromPath.id) newParams.set('id', drawerFromPath.id);
            changed = true;
        }

        if (changed) {
            setSearchParams(newParams, { replace: true });
        }
    }, [location.pathname, searchParams, setSearchParams]);

    // Tab state managed via query param
    const activeTab = searchParams.get('tab') || 'cycles';

    // Drawer state managed via query param
    const action = searchParams.get('action');
    const actionId = searchParams.get('id');

    const tabs = [
        { id: 'cycles', label: 'Cycles', icon: RefreshCw },
        { id: 'goals', label: 'Goals', icon: Target },
        { id: 'reviews', label: 'Performance Reviews', icon: Star },
        { id: 'outcomes', label: 'Outcomes', icon: Award },
    ];

    const handleTabChange = (tabId) => {
        setSearchParams({ tab: tabId });
    };

    const closeDrawer = useCallback(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id');
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'cycles':
                return (
                    <PermissionGate permission="performance" action="read" showFallback fallback={<AccessDenied />}>
                        <PerformanceCycles />
                    </PermissionGate>
                );
            case 'goals':
                return (
                    <PermissionGate permission="performance" action="read" showFallback fallback={<AccessDenied />}>
                        <GoalManagement />
                    </PermissionGate>
                );
            case 'reviews':
                return (
                    <PermissionGate permission="performance" action="read" showFallback fallback={<AccessDenied />}>
                        <ReviewManagement />
                    </PermissionGate>
                );

            case 'outcomes':
                return (
                    <PermissionGate permission="performance" action="read" showFallback fallback={<AccessDenied />}>
                        <PerformanceOutcomes />
                    </PermissionGate>
                );
            default:
                return (
                    <PermissionGate permission="performance" action="read" showFallback fallback={<AccessDenied />}>
                        <PerformanceCycles />
                    </PermissionGate>
                );
        }
    };

    const getDrawerTitle = () => {
        switch (action) {
            case 'createCycle': return 'Create Cycle';
            case 'viewCycle': return 'Performance Cycle Details';
            case 'editCycle': return 'Edit Performance Cycle';
            case 'createGoal': return 'Create New Goal';
            case 'viewGoal': return 'Goal Details';
            case 'editGoal': return 'Edit Goal';
            case 'createReview': return 'Create Review';
            case 'viewReview': return 'Review Details';
            case 'editReview': return 'Edit Review';
            case 'createOutcome': return 'Create Outcome';
            case 'viewOutcome': return 'Outcome Details';
            case 'editOutcome': return 'Edit Outcome';
            default: return '';
        }
    };

    const renderDrawerContent = () => {
        if (!action) return null;

        switch (action) {
            case 'createCycle':
                return <PerformanceCycles initialMode="create" onClose={closeDrawer} isDrawer />;
            case 'viewCycle':
                return <PerformanceCycleDetail cycleId={actionId} onClose={closeDrawer} isDrawer />;
            case 'editCycle':
                return <PerformanceCycles initialMode="edit" cycleId={actionId} onClose={closeDrawer} isDrawer />;
            case 'createGoal':
                return <GoalManagement initialMode="create" onClose={closeDrawer} />;
            case 'viewGoal':
                return <GoalManagement initialMode="view" id={actionId} onClose={closeDrawer} />;
            case 'editGoal':
                return <GoalManagement initialMode="edit" id={actionId} onClose={closeDrawer} />;
            case 'createReview':
                return <ReviewManagement initialMode="create" onClose={closeDrawer} />;
            case 'viewReview':
                return <ReviewManagement initialMode="view" id={actionId} onClose={closeDrawer} />;
            case 'editReview':
                return <ReviewManagement initialMode="edit" id={actionId} onClose={closeDrawer} />;

            case 'createOutcome':
                return <PerformanceOutcomes initialMode="create" onClose={closeDrawer} />;
            case 'viewOutcome':
                return <PerformanceOutcomes initialMode="view" id={actionId} onClose={closeDrawer} />;
            case 'editOutcome':
                return <PerformanceOutcomes initialMode="edit" id={actionId} onClose={closeDrawer} />;
            default:
                return (
                    <div className="p-6 text-center">
                        <p className="text-gray-500">Component for action "{action}" will be integrated soon.</p>
                    </div>
                );
        }
    };

    const isFullScreenDrawer = ['createCycle', 'editCycle', 'viewCycle'].includes(action);

    // If we are on the detail page (and not edit), render the detail component directly
    const isCycleDetailView = location.pathname.includes('/performance_cycle_detail/') && !location.pathname.endsWith('/edit');

    if (isCycleDetailView) {
        return (
            <PermissionGate permission="performance" action="read" showFallback fallback={<AccessDenied />}>
                <PerformanceCycleDetail />
            </PermissionGate>
        );
    }



    const isGoalDetailView = location.pathname.includes('/goal_detail/');

    if (isGoalDetailView) {
        return (
            <PermissionGate permission="performance" action="read" showFallback fallback={<AccessDenied />}>
                <GoalDetail />
            </PermissionGate>
        );
    }


    return (
        <>
            <ModuleLayout
                title="Performance"
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            >
                {renderTabContent()}
            </ModuleLayout>

            <Drawer
                isOpen={!!action}
                onClose={closeDrawer}
                title={getDrawerTitle()}
                size={isFullScreenDrawer ? 'full' : 'xl'}
            >
                {renderDrawerContent()}
            </Drawer>
        </>
    );
};

export default PerformanceModule;
